import { useTranslation } from 'module/hooks/use-translation';
import { useSearchValueState } from 'module/ui/components/atoms/input/search-value-state';
import {
  faUser,
  faChevronDown,
  faChevronUp,
} from '@fortawesome/pro-solid-svg-icons';
import React, { useMemo } from 'react';
import { SavedViews, SharedViews } from 'components/organisms/views';
import { useAllSignalsContext } from './all-signals-context';
import { sidebarMenuItems, myAccountSignalTypes } from './constants';
import {
  AllSignalsSidebarStyled,
  SidebarSearchWrapper,
  StyledSearchInput,
  SidebarMenuSection,
  SidebarMenuItem,
  SidebarDivider,
  SectionHeader,
  SectionSubItem,
  SectionSubItemLabel,
  StyledMenuIcon,
  NoResultsMessage,
  FlexSpacer,
} from './styles';
import { useSavedViews } from './use-saved-views';

interface AllSignalsSidebarProps {
  searchPlaceholder: string;
}

export const AllSignalsSidebar = ({
  searchPlaceholder,
}: AllSignalsSidebarProps) => {
  const noResultsText = useTranslation('all_signals.sidebar.no_results');
  const myAccountsText = useTranslation('all_signals.sidebar.my_accounts');

  const {
    current,
    myAccountsOpen,
    savedViewsSelected,
    sharedViewsSelected,
    selectedSignalUnderMyAccounts,
    isSidebarOpen,
    handleSelect,
    handleMyAccountSignalSelect,
    setMyAccountsOpen,
    setSavedViewsSelected,
    setSharedViewsSelected,
    setSelectedSignalUnderMyAccounts,
    replaceFilter,
  } = useAllSignalsContext();

  const savedViewsResult = useSavedViews({
    setSelectedSignalUnderMyAccounts,
    setSavedViewsSelected,
    setSharedViewsSelected,
    replaceFilter,
    savedViewsSelected,
    sharedViewsSelected,
  });

  const { views, handleSavedViewSelect, handleUpdateView } = savedViewsResult;

  const {
    value: searchQuery,
    onChange: onSearchChange,
    clear: clearSearch,
  } = useSearchValueState();

  const filteredSidebarItems = useMemo(() => {
    if (!searchQuery.trim()) {
      return sidebarMenuItems;
    }

    return sidebarMenuItems.filter((item) =>
      item.label.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }, [searchQuery]);

  const filteredMyAccountSignalTypes = useMemo(() => {
    if (!searchQuery.trim()) {
      return myAccountSignalTypes;
    }

    return myAccountSignalTypes.filter((item) =>
      item.label.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }, [searchQuery]);

  const filteredSavedViews = useMemo(() => {
    if (!searchQuery.trim()) {
      return views?.filter((v) => v.self) || [];
    }

    return (
      views?.filter(
        (v) =>
          v.self &&
          v.viewName?.toLowerCase().includes(searchQuery.toLowerCase()),
      ) || []
    );
  }, [views, searchQuery]);

  const filteredSharedViews = useMemo(() => {
    if (!searchQuery.trim()) {
      return views?.filter((v) => !v.self) || [];
    }

    return (
      views?.filter(
        (v) =>
          !v.self &&
          v.viewName?.toLowerCase().includes(searchQuery.toLowerCase()),
      ) || []
    );
  }, [views, searchQuery]);

  const hasAnyContent = useMemo(() => {
    return (
      filteredSidebarItems.length > 0 ||
      filteredMyAccountSignalTypes.length > 0 ||
      filteredSavedViews.length > 0 ||
      filteredSharedViews.length > 0
    );
  }, [
    filteredSidebarItems,
    filteredMyAccountSignalTypes,
    filteredSavedViews,
    filteredSharedViews,
  ]);

  const shouldShowSection = (sectionItems: any[]) =>
    searchQuery.trim() === '' || sectionItems.length > 0;

  if (!isSidebarOpen) {
    return null;
  }

  return (
    <AllSignalsSidebarStyled>
      <SidebarSearchWrapper>
        <StyledSearchInput
          placeholder={searchPlaceholder}
          value={searchQuery}
          onChange={onSearchChange}
          onClear={clearSearch}
        />
      </SidebarSearchWrapper>

      {/* Show no results message if search has no matches */}
      {searchQuery.trim() && !hasAnyContent && (
        <SidebarMenuSection>
          <NoResultsMessage>{noResultsText}</NoResultsMessage>
        </SidebarMenuSection>
      )}

      {/* Primary Navigation - only show if has matches or no search */}
      {shouldShowSection(filteredSidebarItems) && (
        <>
          <SidebarMenuSection>
            {filteredSidebarItems.map((item) => (
              <SidebarMenuItem
                key={item.key}
                selected={current === item.key}
                onClick={() => handleSelect(item.key)}
              >
                <StyledMenuIcon icon={item.icon} size="sm" />
                {item.label}
              </SidebarMenuItem>
            ))}
          </SidebarMenuSection>

          <SidebarDivider />
        </>
      )}

      {/* My Accounts Section - only show if has matches or no search */}
      {shouldShowSection(filteredMyAccountSignalTypes) && (
        <>
          <SidebarMenuSection>
            <SectionHeader onClick={() => setMyAccountsOpen(!myAccountsOpen)}>
              <StyledMenuIcon icon={faUser} size="sm" />
              {myAccountsText}
              <FlexSpacer />
              <StyledMenuIcon
                icon={myAccountsOpen ? faChevronUp : faChevronDown}
                size="sm"
              />
            </SectionHeader>
            <div>
              {myAccountsOpen && (
                <>
                  {filteredMyAccountSignalTypes.map((item) => (
                    <SectionSubItem
                      key={item.key}
                      selected={selectedSignalUnderMyAccounts === item.key}
                      onClick={() => handleMyAccountSignalSelect(item.key)}
                    >
                      <SectionSubItemLabel>
                        <StyledMenuIcon icon={item.icon} size="sm" />
                        {item.label}
                      </SectionSubItemLabel>
                    </SectionSubItem>
                  ))}
                </>
              )}
            </div>
          </SidebarMenuSection>

          <SidebarDivider />
        </>
      )}

      {/* Saved Views Section - only show if has matches or no search */}
      {shouldShowSection(filteredSavedViews) && (
        <>
          <SavedViews
            views={filteredSavedViews}
            selectedView={savedViewsSelected}
            onViewSelect={handleSavedViewSelect}
            onUpdateView={handleUpdateView}
          />
          <SidebarDivider />
        </>
      )}

      {/* Shared Views Section - only show if has matches or no search */}
      {shouldShowSection(filteredSharedViews) && (
        <SharedViews
          views={filteredSharedViews}
          selectedView={sharedViewsSelected}
          onViewSelect={handleSavedViewSelect}
          onUpdateView={handleUpdateView}
        />
      )}
    </AllSignalsSidebarStyled>
  );
};
